// context/WishlistContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import API from '../api/MainApi';

const WISHLIST_COOKIE = 'wishlist';
const WishlistContext = createContext();

export const useWishlistContext = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }) => {
  const [items, setItems] = useState([]); // { id, quantity }
  const [products, setProducts] = useState([]); // полные объекты товаров
  const [loading, setLoading] = useState(false);

  const getItemsFromCookie = useCallback(() => {
    const cookie = Cookies.get(WISHLIST_COOKIE);
    return cookie ? JSON.parse(cookie) : [];
  }, []);

  const setCookie = useCallback((newItems) => {
    Cookies.set(WISHLIST_COOKIE, JSON.stringify(newItems), { expires: 365, path: '/' });
  }, []);

  const loadProducts = useCallback(async (itemsArray) => {
    if (!itemsArray.length) {
      setProducts([]);
      return;
    }
    setLoading(true);
    try {
      const ids = itemsArray.map(item => item.id);
      const res = await API.getProductsByIds(ids);
      const productsData = res.data;
      // Прикрепляем quantity к каждому продукту
      const productsWithQuantity = productsData.map(product => {
        const item = itemsArray.find(i => i.id === product._id);
        return { ...product, quantity: item?.quantity || 1 };
      });
      setProducts(productsWithQuantity);
    } catch (error) {
      console.error('Ошибка загрузки товаров вишлиста:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    const cookieItems = getItemsFromCookie();
    setItems(cookieItems);
    loadProducts(cookieItems);
  }, [getItemsFromCookie, loadProducts]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addToWishlist = useCallback(async (product) => {
    const currentItems = getItemsFromCookie();
    const existingIndex = currentItems.findIndex(item => item.id === product._id);
    let newItems;
    if (existingIndex >= 0) {
      if (product.isMany) {
        // увеличиваем количество
        newItems = [...currentItems];
        newItems[existingIndex].quantity += 1;
      } else {
        // заменяем (количество всегда 1)
        newItems = currentItems.map(item =>
          item.id === product._id ? { ...item, quantity: 1 } : item
        );
      }
    } else {
      newItems = [...currentItems, { id: product._id, quantity: 1 }];
    }
    setCookie(newItems);
    setItems(newItems);
    await loadProducts(newItems);
  }, [getItemsFromCookie, setCookie, loadProducts]);

  const removeItem = useCallback((productId) => {
    const currentItems = getItemsFromCookie();
    const newItems = currentItems.filter(item => item.id !== productId);
    setCookie(newItems);
    setItems(newItems);
    setProducts(prev => prev.filter(p => p._id !== productId));
  }, [getItemsFromCookie, setCookie]);

  const updateQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity < 1) return;
    const currentItems = getItemsFromCookie();
    const itemIndex = currentItems.findIndex(item => item.id === productId);
    if (itemIndex === -1) return;
    const newItems = [...currentItems];
    newItems[itemIndex].quantity = newQuantity;
    setCookie(newItems);
    setItems(newItems);
    setProducts(prev => prev.map(p =>
      p._id === productId ? { ...p, quantity: newQuantity } : p
    ));
  }, [getItemsFromCookie, setCookie]);

  const isInWishlist = useCallback((productId) => {
    return items.some(item => item.id === productId);
  }, [items]);

  const toggleWishlist = useCallback((product) => {
    if (isInWishlist(product._id)) {
      removeItem(product._id);
    } else {
      addToWishlist(product);
    }
  }, [isInWishlist, removeItem, addToWishlist]);

  const total = products.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0);

  return (
    <WishlistContext.Provider value={{
      items,
      products,
      loading,
      addToWishlist,
      removeItem,
      updateQuantity,
      isInWishlist,
      toggleWishlist,
      refresh,
      total
    }}>
      {children}
    </WishlistContext.Provider>
  );
};